// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    AlertCircle,
    ArrowRight,
} from "lucide-react";
import CampaignsHeader from "./CampaignsHeader";
import { Page } from "components/ui/page";
import CreateCampaignModal from "./CreateCampaignModal";
import CampaignBulkOperations from "./CampaignBulkOperations";
import CampaignABTestModal from "./CampaignABTestModal";
import AIConfigurationModal from "./AIConfigurationModal";
import CampaignDetailsModal from "./CampaignDetailsModal";
import CampaignEditModal from "./CampaignEditModal";
import {
    BulkDeleteCampaignDialog,
    DeleteCampaignDialog,
    BulkStatusChangeDialog,
    BulkDuplicateDialog,
} from "./CampaignConfirmationDialogs";
import { ESortOrder, useCampaigns, useDeleteCampaign } from "core";
import CampaignView from "./CampaignView";
import { toast } from "components/ui/toast";
import { isDevMode } from "lib/featureFlags";


class CampaignErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: 0,
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error(
            "Campaign Error Boundary caught an error:",
            error,
            errorInfo,
        );

        this.setState({
            error: error,
            errorInfo: errorInfo,
        });
    }

    handleRetry = () => {
        this.setState((prevState) => ({
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: prevState.retryCount + 1,
        }));
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="bg-surface rounded-xl shadow-sm border border-red-100 dark:border-red-800/50">
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
                                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-content">
                                    Campaigns Unavailable
                                </h3>
                                <p className="text-sm text-content-muted">
                                    We're having trouble loading your campaigns.
                                </p>
                            </div>
                        </div>

                        {process.env.NODE_ENV === "development" &&
                            this.state.error && (
                                <details className="mb-4">
                                    <summary className="cursor-pointer text-sm font-medium text-red-800 dark:text-red-300 hover:text-red-900 dark:hover:text-red-300">
                                        ▶ Debug Info (dev only)
                                    </summary>
                                    <div className="mt-2 p-3 bg-red-100 dark:bg-red-900/30 rounded border text-xs font-mono text-red-800 dark:text-red-300 overflow-auto">
                                        <div className="mb-2">
                                            <strong>Error:</strong>{" "}
                                            {this.state.error.toString()}
                                        </div>
                                        {this.state.errorInfo && (
                                            <div>
                                                <strong>Component Stack:</strong>
                                                <pre className="whitespace-pre-wrap mt-1">
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </details>
                            )}

                        <div className="flex items-center gap-3 pt-4 border-t border-line-subtle">
                            <button
                                onClick={this.handleRetry}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <ArrowRight className="w-3.5 h-3.5" />
                                Try Again
                            </button>

                            <button
                                onClick={this.handleReload}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                            >
                                <ArrowRight className="w-3.5 h-3.5" />
                                Reload Page
                            </button>
                        </div>

                        {this.state.retryCount > 0 && (
                            <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-100 dark:border-yellow-800/50 rounded-lg">
                                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                                    <strong>
                                        Retry attempt #{this.state.retryCount}
                                    </strong>
                                    {this.state.retryCount >= 3 && (
                                        <span className="block mt-1 text-yellow-700 dark:text-yellow-300">
                                            Still not working? Please reload the page or contact support.
                                        </span>
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const ITEMS_PER_PAGE = 50;

const EnhancedCampaignsView = ({
    campaigns = [],
    aiAgents = [],
    templates = [],
    abTests = [],
    onCreateCampaign,
    onEditCampaign,
    onDeleteCampaign,
    onDuplicateCampaign,
    onPauseCampaign,
    onResumeCampaign,
    onViewDetails,
    onConfigureAI,
    onViewABTests,
    onCreateABTest,
    onViewABTestDetails,
    onViewDecisionTrail,
    loading = false,
    error = null,
}) => {
    // State management following established patterns
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [aiGeneratedFilter, setAiGeneratedFilter] = useState("all");
    const [performanceFilter, setPerformanceFilter] = useState("all");
    const [dateRangeFilter, setDateRangeFilter] = useState("all");
    const [sortBy, setSortBy] = useState("updatedAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState("list"); // 'grid' or 'list'
    const [currentPage, setCurrentPage] = useState(1);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showABTestModal, setShowABTestModal] = useState(false);
    const [selectedCampaignForABTest, setSelectedCampaignForABTest] =
        useState(null);
    const [showAIConfigModal, setShowAIConfigModal] = useState(false);
    const [selectedCampaignForAIConfig, setSelectedCampaignForAIConfig] =
        useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCampaignForDetails, setSelectedCampaignForDetails] =
        useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCampaignForEdit, setSelectedCampaignForEdit] =
        useState(null);

    // Bulk operations and confirmation dialog state
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
    const [showBulkDuplicateDialog, setShowBulkDuplicateDialog] =
        useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [bulkStatusChange, setBulkStatusChange] = useState(null);
    const [bulkOperationLoading, setBulkOperationLoading] = useState(false);

    // ************************** CAMPAIGN DATA AND FILTER **********************************
    // Status filter state - "all" mode or specific filters
    const [isAllStatusSelected, setIsAllStatusSelected] = React.useState(true);

    const [campaignFilter, setCampaignFilter] = React.useState({
        page: 1,
        active: undefined,
        progress: undefined,
        pc: undefined,
        comp: undefined,
        terminated: undefined,
        sortBy: "created_at",
        order: ESortOrder.Desc,
    });
    const {
        data: campaignData,
        isFetching: campaignLoading,
        isPlaceholderData,
        isError: campaignError,
        error: campaignErrorObj,
        refetch: refetchCampaigns,
    } = useCampaigns(campaignFilter);
    const deleteCampaignMutation = useDeleteCampaign();

    // Check if all filters are undefined (none selected)
    const isNoFilterSelected = !campaignFilter.active && !campaignFilter.progress &&
        !campaignFilter.pc && !campaignFilter.comp &&
        !campaignFilter.terminated;

    // Handle "All" checkbox toggle
    const handleAllStatusToggle = (checked) => {
        setIsAllStatusSelected(checked);
        if (checked) {
            // When "All" is checked, clear all individual status filters
            setCampaignFilter((prev) => ({
                ...prev,
                active: undefined,
                progress: undefined,
                pc: undefined,
                comp: undefined,
                terminated: undefined,
                page: 1,
            }));
        }
    };

    // Toggle individual status filter
    const toggleCampaignFilter = (key, value) => {
        // If checking a specific status, uncheck "All"
        if (value) {
            setIsAllStatusSelected(false);
        }

        setCampaignFilter((prev) => {
            const newFilter = {
                ...prev,
                [key]: value ? 1 : undefined,
                page: 1,
            };

            // Check if all individual filters are now unchecked
            const allUnchecked = !newFilter.active && !newFilter.progress &&
                !newFilter.pc && !newFilter.comp &&
                !newFilter.terminated;

            // If all are unchecked, automatically check "All"
            if (allUnchecked) {
                setIsAllStatusSelected(true);
            }

            return newFilter;
        });
    };


    const [segments, setSegments] = useState([         // BYPASSED THIS COMPONENTS
        {
            id: "1",
            name: "High-Value Customers",
            description: "Customers with high purchase value and engagement",
            size: 2847,
            count: 2847, // Keep for backward compatibility
            growth: "+12%",
            color: "green",
            aiGenerated: true,
            createdBy: "ai",
            criteria: [
                {
                    id: "1",
                    field: "leadScore",
                    operator: "greater_than",
                    value: 80,
                    weight: 1,
                },
                {
                    id: "2",
                    field: "status",
                    operator: "equals",
                    value: "hot",
                    weight: 1,
                    logicalOperator: "AND",
                },
            ],
            manualLeadIds: [], // Array to store manually added lead IDs
            status: "active",
            tags: ["high-value", "priority"],
            createdAt: new Date("2025-07-01"),
            updatedAt: new Date("2025-07-10"),
            usageCount: 15,
            lastUsed: new Date("2025-07-09"),
            expectedPerformance: {
                openRate: 0.35,
                clickRate: 0.08,
                conversionRate: 0.06,
            },
            campaignResults: [
                {
                    campaignId: 1,
                    campaignName: "Summer Sale 2024",
                    sent: 2847,
                    opened: 1896,
                    clicked: 432,
                    revenue: 8640,
                },
                {
                    campaignId: 2,
                    campaignName: "Welcome Series",
                    sent: 1200,
                    opened: 756,
                    clicked: 151,
                    revenue: 2280,
                },
            ],
        },
        {
            id: "2",
            name: "Recent Purchasers",
            description: "Leads who have made recent purchases",
            size: 1923,
            count: 1923,
            growth: "+8%",
            color: "blue",
            aiGenerated: false,
            createdBy: "user",
            criteria: [
                {
                    id: "1",
                    field: "lastEngaged",
                    operator: "greater_than",
                    value: "2025-06-01",
                    weight: 1,
                },
            ],
            manualLeadIds: [], // Array to store manually added lead IDs
            status: "active",
            tags: ["recent", "purchasers"],
            createdAt: new Date("2025-06-15"),
            updatedAt: new Date("2025-07-08"),
            usageCount: 8,
            lastUsed: new Date("2025-07-05"),
            expectedPerformance: {
                openRate: 0.28,
                clickRate: 0.06,
                conversionRate: 0.04,
            },
            campaignResults: [
                {
                    campaignId: 2,
                    campaignName: "Welcome Series",
                    sent: 1923,
                    opened: 1154,
                    clicked: 231,
                    revenue: 3846,
                },
            ],
        },
        {
            id: "3",
            name: "Inactive Users",
            description: "Users who haven't engaged recently",
            size: 5621,
            count: 5621,
            growth: "-3%",
            color: "red",
            aiGenerated: true,
            createdBy: "ai",
            criteria: [
                {
                    id: "1",
                    field: "lastEngaged",
                    operator: "less_than",
                    value: "2025-05-01",
                    weight: 1,
                },
            ],
            manualLeadIds: [], // Array to store manually added lead IDs
            status: "active",
            tags: ["inactive", "nurture"],
            createdAt: new Date("2025-05-20"),
            updatedAt: new Date("2025-07-07"),
            usageCount: 3,
            lastUsed: new Date("2025-06-20"),
            expectedPerformance: {
                openRate: 0.15,
                clickRate: 0.02,
                conversionRate: 0.01,
            },
            campaignResults: [
                {
                    campaignId: 3,
                    campaignName: "Re-engagement Campaign",
                    sent: 5621,
                    opened: 843,
                    clicked: 112,
                    revenue: 560,
                },
            ],
        },
        {
            id: "4",
            name: "Newsletter Subscribers",
            description: "Active newsletter subscribers",
            size: 12450,
            count: 12450,
            growth: "+15%",
            color: "purple",
            aiGenerated: false,
            createdBy: "user",
            criteria: [
                {
                    id: "1",
                    field: "tags",
                    operator: "contains",
                    value: "Newsletter",
                    weight: 1,
                },
            ],
            manualLeadIds: [], // Array to store manually added lead IDs
            status: "active",
            tags: ["newsletter", "subscribers"],
            createdAt: new Date("2025-06-01"),
            updatedAt: new Date("2025-07-12"),
            usageCount: 22,
            lastUsed: new Date("2025-07-12"),
            expectedPerformance: {
                openRate: 0.32,
                clickRate: 0.07,
                conversionRate: 0.03,
            },
            campaignResults: [
                {
                    campaignId: 1,
                    campaignName: "Summer Sale 2024",
                    sent: 8500,
                    opened: 2720,
                    clicked: 595,
                    revenue: 1785,
                },
                {
                    campaignId: 2,
                    campaignName: "Welcome Series",
                    sent: 3950,
                    opened: 1264,
                    clicked: 276,
                    revenue: 1185,
                },
            ],
        },
        {
            id: "5",
            name: "Product Viewers",
            description: "Leads who have viewed products recently",
            size: 3456,
            count: 3456,
            growth: "+5%",
            color: "orange",
            aiGenerated: true,
            createdBy: "ai",
            criteria: [
                {
                    id: "1",
                    field: "leadScore",
                    operator: "greater_than",
                    value: 50,
                    weight: 1,
                },
                {
                    id: "2",
                    field: "lastEngaged",
                    operator: "greater_than",
                    value: "2025-06-15",
                    weight: 1,
                    logicalOperator: "AND",
                },
            ],
            manualLeadIds: [], // Array to store manually added lead IDs
            status: "active",
            tags: ["product-viewers", "engaged"],
            createdAt: new Date("2025-07-05"),
            updatedAt: new Date("2025-07-11"),
            usageCount: 5,
            lastUsed: new Date("2025-07-10"),
            expectedPerformance: {
                openRate: 0.25,
                clickRate: 0.05,
                conversionRate: 0.025,
            },
            campaignResults: [
                {
                    campaignId: 1,
                    campaignName: "Summer Sale 2024",
                    sent: 2100,
                    opened: 525,
                    clicked: 105,
                    revenue: 525,
                },
            ],
        },
        {
            id: "6",
            name: "Cart Abandoners",
            description: "Users who abandoned their shopping carts",
            size: 892,
            count: 892,
            growth: "+2%",
            color: "yellow",
            aiGenerated: true,
            createdBy: "ai",
            criteria: [
                {
                    id: "1",
                    field: "tags",
                    operator: "contains",
                    value: "cart-abandon",
                    weight: 1,
                },
            ],
            manualLeadIds: [], // Array to store manually added lead IDs
            status: "active",
            tags: ["cart-abandon", "recovery"],
            createdAt: new Date("2025-07-08"),
            updatedAt: new Date("2025-07-13"),
            usageCount: 2,
            lastUsed: new Date("2025-07-11"),
            expectedPerformance: {
                openRate: 0.22,
                clickRate: 0.04,
                conversionRate: 0.08,
            },
            campaignResults: [
                {
                    campaignId: 1,
                    campaignName: "Cart Recovery Campaign",
                    sent: 892,
                    opened: 196,
                    clicked: 36,
                    revenue: 720,
                },
            ],
        },
    ]);
    const [newLead, setNewLead] = useState({
        name: "",
        email: "",
        phone: "",
        location: "",
        source: "",
        tags: "",
    });
    const [newCampaign, setNewCampaign] = useState({
        name: "",
        type: "promotional",
        template: "",
        audience: "",
        segmentId: "",
        estimatedReach: 0,
    });
    const uniqueTags = useMemo(() => {
        const tagSet = new Set();
        campaigns.forEach((campaign) => {
            if (campaign.tags && Array.isArray(campaign.tags)) {
                campaign.tags.forEach((tag) => tagSet.add(tag));
            }
        });
        return Array.from(tagSet).sort();
    }, [campaigns]);

    // Performance calculation helper
    const getCampaignPerformanceLevel = useCallback((campaign) => {
        const { openRate, clickRate, conversionRate, roi } = campaign.metrics;

        const scores = [
            openRate >= 25 ? 2 : openRate >= 15 ? 1 : 0,
            clickRate >= 5 ? 2 : clickRate >= 2.5 ? 1 : 0,
            conversionRate >= 2 ? 2 : conversionRate >= 1 ? 1 : 0,
            roi >= 300 ? 2 : roi >= 150 ? 1 : 0,
        ];

        const averageScore =
            scores.reduce((sum, score) => sum + score, 0) / scores.length;

        if (averageScore >= 1.5) return "high";
        if (averageScore >= 0.75) return "medium";
        return "low";
    }, []);

    // Memoized filter functions for better performance
    const searchFilteredCampaigns = useMemo(() => {
        if (!searchTerm) return campaigns;

        const searchLower = searchTerm.toLowerCase();
        return campaigns.filter(
            (campaign) =>
                campaign.name.toLowerCase().includes(searchLower) ||
                (campaign.description &&
                    campaign.description.toLowerCase().includes(searchLower)) ||
                campaign.templateName.toLowerCase().includes(searchLower) ||
                campaign.segmentName.toLowerCase().includes(searchLower) ||
                campaign.tags.some((tag) =>
                    tag.toLowerCase().includes(searchLower),
                ),
        );
    }, [campaigns, searchTerm]);

    const statusFilteredCampaigns = useMemo(() => {
        if (statusFilter === "all") return searchFilteredCampaigns;
        return searchFilteredCampaigns.filter(
            (campaign) => campaign.status === statusFilter,
        );
    }, [searchFilteredCampaigns, statusFilter]);

    const typeFilteredCampaigns = useMemo(() => {
        if (typeFilter === "all") return statusFilteredCampaigns;
        return statusFilteredCampaigns.filter(
            (campaign) => campaign.type === typeFilter,
        );
    }, [statusFilteredCampaigns, typeFilter]);

    const aiFilteredCampaigns = useMemo(() => {
        if (aiGeneratedFilter === "all") return typeFilteredCampaigns;
        return typeFilteredCampaigns.filter(
            (campaign) =>
                campaign.aiGenerated === (aiGeneratedFilter === "true"),
        );
    }, [typeFilteredCampaigns, aiGeneratedFilter]);

    const performanceFilteredCampaigns = useMemo(() => {
        if (performanceFilter === "all") return aiFilteredCampaigns;
        return aiFilteredCampaigns.filter(
            (campaign) =>
                getCampaignPerformanceLevel(campaign) === performanceFilter,
        );
    }, [aiFilteredCampaigns, performanceFilter, getCampaignPerformanceLevel]);

    const dateFilteredCampaigns = useMemo(() => {
        if (dateRangeFilter === "all") return performanceFilteredCampaigns;

        const now = new Date();
        let startDate;

        switch (dateRangeFilter) {
            case "week":
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case "month":
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case "quarter":
                startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                break;
            case "year":
                startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                break;
            default:
                return performanceFilteredCampaigns;
        }

        return performanceFilteredCampaigns.filter((campaign) => {
            const campaignDate = new Date(campaign.createdAt);
            return campaignDate >= startDate;
        });
    }, [performanceFilteredCampaigns, dateRangeFilter]);

    // Memoized sorting function
    const filteredAndSortedCampaigns = useMemo(() => {
        const filtered = [...dateFilteredCampaigns];

        // Sort campaigns
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case "name":
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case "createdAt":
                    aValue = new Date(a.createdAt);
                    bValue = new Date(b.createdAt);
                    break;
                case "updatedAt":
                    aValue = new Date(a.updatedAt || a.createdAt);
                    bValue = new Date(b.updatedAt || b.createdAt);
                    break;
                case "performance":
                    const aPerf = getCampaignPerformanceLevel(a);
                    const bPerf = getCampaignPerformanceLevel(b);
                    const perfOrder = { high: 3, medium: 2, low: 1 };
                    aValue = perfOrder[aPerf] || 0;
                    bValue = perfOrder[bPerf] || 0;
                    break;
                case "status":
                    const statusOrder = {
                        active: 5,
                        scheduled: 4,
                        draft: 3,
                        paused: 2,
                        completed: 1,
                        archived: 0,
                    };
                    aValue = statusOrder[a.status] || 0;
                    bValue = statusOrder[b.status] || 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
            if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [dateFilteredCampaigns, sortBy, sortOrder, getCampaignPerformanceLevel]);

    // For virtual scrolling, we don't need pagination but keep for backward compatibility
    const totalPages = Math.ceil(
        filteredAndSortedCampaigns.length / ITEMS_PER_PAGE,
    );
    const paginatedCampaigns = filteredAndSortedCampaigns; // Use all campaigns for virtual scrolling

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchTerm,
        statusFilter,
        typeFilter,
        aiGeneratedFilter,
        performanceFilter,
        dateRangeFilter,
    ]);

    // Selection handlers
    const handleCampaignSelect = useCallback(
        (campaignId) => {
            const newSelected = new Set(selectedCampaigns);
            if (newSelected.has(campaignId)) {
                newSelected.delete(campaignId);
            } else {
                newSelected.add(campaignId);
            }
            setSelectedCampaigns(newSelected);
        },
        [selectedCampaigns],
    );

    const handleSelectAll = useCallback(() => {
        const actualCampaigns = campaignData?.items || [];
        if (selectedCampaigns.size === actualCampaigns.length && actualCampaigns.length > 0) {
            setSelectedCampaigns(new Set());
        } else {
            setSelectedCampaigns(
                new Set(
                    actualCampaigns.map((campaign) => campaign.id),
                ),
            );
        }
    }, [selectedCampaigns.size, campaignData]);

    // Clear filters
    const clearFilters = useCallback(() => {
        setSearchTerm("");
        setStatusFilter("all");
        setTypeFilter("all");
        setAiGeneratedFilter("all");
        setPerformanceFilter("all");
        setDateRangeFilter("all");
    }, []);

    // Modal handlers
    const handleCreateCampaign = useCallback(() => {
        setShowCreateModal(true);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
        setShowCreateModal(false);
    }, []);

    const handleSaveCampaign = useCallback(
        async (campaignData) => {
            try {
                await onCreateCampaign(campaignData);
                setShowCreateModal(false);
            } catch (error) {
                console.error("Error creating campaign:", error);
                // Error handling is done in the modal
                throw error;
            }
        },
        [onCreateCampaign],
    );

    // Get selected campaign objects - use real API data
    const selectedCampaignObjects = useMemo(() => {
        const actualCampaigns = campaignData?.items || [];
        return actualCampaigns.filter((campaign) =>
            selectedCampaigns.has(campaign.id),
        );
    }, [campaignData, selectedCampaigns]);

    // Bulk operations handlers
    const handleBulkAction = useCallback(async (action, data) => {
        setBulkOperationLoading(true);

        try {
            switch (action) {
                case "status":
                    setBulkStatusChange(data.status);
                    setShowBulkStatusDialog(true);
                    break;
                case "duplicate":
                    setShowBulkDuplicateDialog(true);
                    break;
                case "delete":
                    setShowBulkDeleteDialog(true);
                    break;
                default:
                    console.warn("Unknown bulk action:", action);
            }
        } catch (error) {
            console.error("Error handling bulk action:", error);
        } finally {
            setBulkOperationLoading(false);
        }
    }, []);

    const handleBulkStatusChange = useCallback(async () => {
        setBulkOperationLoading(true);

        try {
            // Process each selected campaign
            for (const campaignId of selectedCampaigns) {
                const campaign = campaigns.find((c) => c.id === campaignId);
                if (campaign) {
                    if (bulkStatusChange === "active") {
                        await onResumeCampaign(campaignId);
                    } else if (bulkStatusChange === "paused") {
                        await onPauseCampaign(campaignId);
                    }
                    // Note: Archive functionality would need to be added to the parent component
                }
            }

            // Clear selection and close dialog
            setSelectedCampaigns(new Set());
            setShowBulkStatusDialog(false);
            setBulkStatusChange(null);
        } catch (error) {
            console.error("Error updating campaign statuses:", error);
            // Error handling could show a toast notification here
        } finally {
            setBulkOperationLoading(false);
        }
    }, [
        selectedCampaigns,
        campaigns,
        bulkStatusChange,
        onResumeCampaign,
        onPauseCampaign,
    ]);

    const handleBulkDuplicate = useCallback(async () => {
        setBulkOperationLoading(true);

        try {
            // Process each selected campaign
            for (const campaignId of selectedCampaigns) {
                const campaign = campaigns.find((c) => c.id === campaignId);
                if (campaign) {
                    await onDuplicateCampaign(campaign);
                }
            }

            // Clear selection and close dialog
            setSelectedCampaigns(new Set());
            setShowBulkDuplicateDialog(false);
        } catch (error) {
            console.error("Error duplicating campaigns:", error);
            // Error handling could show a toast notification here
        } finally {
            setBulkOperationLoading(false);
        }
    }, [selectedCampaigns, campaigns, onDuplicateCampaign]);

    const handleBulkDelete = useCallback(async () => {
        setBulkOperationLoading(true);

        try {
            // Process each selected campaign using the mutation
            for (const campaignId of selectedCampaigns) {
                await deleteCampaignMutation.mutateAsync(campaignId);
            }

            // Call the parent's onDeleteCampaign if provided for additional handling
            if (onDeleteCampaign) {
                for (const campaignId of selectedCampaigns) {
                    await onDeleteCampaign(campaignId);
                }
            }

            // Show success message
            toast.success({
                title: "Campaigns Deleted",
                description: `${selectedCampaigns.size} campaign${selectedCampaigns.size !== 1 ? 's' : ''} successfully deleted.`
            });

            // Clear selection and close dialog
            setSelectedCampaigns(new Set());
            setShowBulkDeleteDialog(false);
        } catch (error) {
            console.error("Error deleting campaigns:", error);

            // Extract error message from response
            const errorMessage = error.response?.data?.message
                || error.response?.data?.error
                || error.message
                || "Failed to delete campaigns. Please try again.";

            // Show error toast
            toast.error({
                title: "Delete Failed",
                description: errorMessage
            });

            // Close the modal even on error
            setShowBulkDeleteDialog(false);
        } finally {
            setBulkOperationLoading(false);
        }
    }, [selectedCampaigns, deleteCampaignMutation, onDeleteCampaign]);

    // Individual campaign delete handler
    const handleDeleteCampaign = useCallback((campaign) => {
        console.log("Delete button clicked for campaign:", campaign);
        setCampaignToDelete(campaign);
        setShowDeleteDialog(true);
    }, []);

    const handleConfirmDeleteCampaign = useCallback(async () => {
        if (!campaignToDelete) return;

        console.log("Deleting campaign:", campaignToDelete);
        console.log("Campaign ID:", campaignToDelete.id);

        setBulkOperationLoading(true);

        try {
            // Use the delete mutation hook
            const result = await deleteCampaignMutation.mutateAsync(campaignToDelete.id);
            console.log("Delete result:", result);

            // Call the parent's onDeleteCampaign if provided for additional handling
            if (onDeleteCampaign) {
                await onDeleteCampaign(campaignToDelete.id);
            }

            // Show success message
            toast.success({
                title: "Campaign Deleted",
                description: `${campaignToDelete.name} has been successfully deleted.`
            });

            setShowDeleteDialog(false);
            setCampaignToDelete(null);
        } catch (error) {
            console.error("Error deleting campaign:", error);
            console.error("Error details:", error.response?.data || error.message);

            // Extract error message from response
            const errorMessage = error.response?.data?.message
                || error.response?.data?.error
                || error.message
                || "Failed to delete campaign. Please try again.";

            // Show error toast
            toast.error({
                title: "Delete Failed",
                description: errorMessage
            });

            // Close the modal
            setShowDeleteDialog(false);
            setCampaignToDelete(null);
        } finally {
            setBulkOperationLoading(false);
        }
    }, [campaignToDelete, deleteCampaignMutation, onDeleteCampaign]);

    // Clear selection handler
    const handleClearSelection = useCallback(() => {
        setSelectedCampaigns(new Set());
    }, []);

    // A/B Testing modal handlers
    const handleViewABTests = useCallback((campaign) => {
        setSelectedCampaignForABTest(campaign);
        setShowABTestModal(true);
    }, []);

    const handleCloseABTestModal = useCallback(() => {
        setShowABTestModal(false);
        setSelectedCampaignForABTest(null);
    }, []);

    const handleCreateABTest = useCallback(
        async (testConfig) => {
            try {
                if (onCreateABTest) {
                    await onCreateABTest(testConfig);
                }
            } catch (error) {
                console.error("Error creating A/B test:", error);
                throw error;
            }
        },
        [onCreateABTest],
    );

    const handleApproveDecision = useCallback(async (decision) => {
        try {
            // This would typically call an API to approve the decision
            console.log("Approving decision:", decision);
            // You could add a callback prop for this functionality
        } catch (error) {
            console.error("Error approving decision:", error);
        }
    }, []);

    const handleRejectDecision = useCallback(async (decision) => {
        try {
            // This would typically call an API to reject the decision
            console.log("Rejecting decision:", decision);
            // You could add a callback prop for this functionality
        } catch (error) {
            console.error("Error rejecting decision:", error);
        }
    }, []);

    const handleImplementWinner = useCallback(
        async (testId, winnerVariantId) => {
            try {
                // This would typically call an API to implement the winning variant
                console.log("Implementing winner:", testId, winnerVariantId);
                // You could add a callback prop for this functionality
            } catch (error) {
                console.error("Error implementing winner:", error);
            }
        },
        [],
    );

    // AI Configuration modal handlers
    const handleConfigureAI = useCallback(
        (campaignId, agentId) => {
            const campaign = campaigns.find((c) => c.id === campaignId);
            if (campaign) {
                setSelectedCampaignForAIConfig(campaign);
                setShowAIConfigModal(true);
            }
        },
        [campaigns],
    );

    const handleCloseAIConfigModal = useCallback(() => {
        setShowAIConfigModal(false);
        setSelectedCampaignForAIConfig(null);
    }, []);

    const handleSaveAIConfiguration = useCallback(
        async (campaignId, config) => {
            try {
                // This would typically call an API to save the AI configuration
                console.log(
                    "Saving AI configuration for campaign:",
                    campaignId,
                    config,
                );

                // Call the parent's onConfigureAI function if it exists
                if (onConfigureAI) {
                    await onConfigureAI(campaignId, config);
                }

                // Show success feedback
                console.log("AI configuration saved successfully");
            } catch (error) {
                console.error("Error saving AI configuration:", error);
                throw error;
            }
        },
        [onConfigureAI],
    );

    // Campaign Details modal handlers
    const handleViewDetails = useCallback((campaign) => {
        setSelectedCampaignForDetails(campaign);
        setShowDetailsModal(true);
    }, []);

    const handleCloseDetailsModal = useCallback(() => {
        setShowDetailsModal(false);
        setSelectedCampaignForDetails(null);
    }, []);

    // Campaign Edit modal handlers
    const handleEditCampaign = useCallback((campaign) => {
        setSelectedCampaignForEdit(campaign);
        setShowEditModal(true);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setShowEditModal(false);
        setSelectedCampaignForEdit(null);
    }, []);

    const handleSaveEditedCampaign = useCallback(
        async (updatedCampaign) => {
            try {
                await onEditCampaign(updatedCampaign);
                setShowEditModal(false);
                setSelectedCampaignForEdit(null);
            } catch (error) {
                console.error("Error updating campaign:", error);
                // Error handling is done in the modal
                throw error;
            }
        },
        [onEditCampaign],
    );

    // Memoized campaign action handlers to prevent unnecessary re-renders
    const memoizedCampaignHandlers = useMemo(
        () => ({
            onEdit: handleEditCampaign,
            onDelete: handleDeleteCampaign,
            onDuplicate: onDuplicateCampaign,
            onPause: onPauseCampaign,
            onResume: onResumeCampaign,
            onViewDetails: handleViewDetails,
            onConfigureAI: handleConfigureAI,
            onViewABTests: handleViewABTests,
            onCreateABTest: handleCreateABTest,
            onSelect: handleCampaignSelect,
        }),
        [
            handleEditCampaign,
            handleDeleteCampaign,
            onDuplicateCampaign,
            onPauseCampaign,
            onResumeCampaign,
            handleViewDetails,
            handleConfigureAI,
            handleViewABTests,
            handleCreateABTest,
            handleCampaignSelect,
        ],
    );

    // Loading state
    if (loading) {
        return (
            <div className="p-6 space-y-6">
                <div className="bg-surface rounded-xl shadow-lg p-6 border border-line-subtle">
                    <div className="animate-pulse">
                        <div className="h-6 bg-gray-200 dark:bg-dark-600 rounded w-1/4 mb-4"></div>
                        <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-1/2 mb-6"></div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {[...Array(4)].map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-gray-100 dark:bg-dark-700 rounded-lg p-4"
                                >
                                    <div className="h-8 bg-gray-200 dark:bg-dark-600 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                        <div className="h-10 bg-gray-200 dark:bg-dark-600 rounded mb-4"></div>
                    </div>
                </div>
                <div className="bg-surface rounded-xl shadow-lg p-6 border border-line-subtle">
                    <div className="animate-pulse">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="border rounded-lg p-4">
                                    <div className="h-6 bg-gray-200 dark:bg-dark-600 rounded mb-3"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4 mb-2"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-1/2"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <h4 className="font-medium text-red-900 dark:text-red-300 mb-1">
                                Error Loading Campaigns
                            </h4>
                            <p className="text-sm text-red-700 dark:text-red-300 mb-2">{error}</p>
                            <p className="text-sm text-red-600 dark:text-red-300">
                                Please try refreshing the page or contact
                                support if the problem persists.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <CampaignErrorBoundary>
            {/* `Page`, not a bare div. This wrapper was `w-full mx-auto px-4
                sm:px-6 lg:px-8 py-6` — Page's own classes minus the one that
                matters, `flex flex-col gap-5`. With no gap container, nothing
                separated the stat strip from the toolbar beneath it, so the two
                cards sat flush while every other gap on the page was 20px.

                `width="full"` keeps the current unbounded width; every other
                page defaults to `wide` (110rem). */}
            <Page width="full">
                {/* Temporary Test Button for A/B Test Modal */}
                {isDevMode() && campaigns.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-medium text-yellow-900 dark:text-yellow-300">
                                    Development Testing
                                </h3>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    Click to test the A/B Testing Modal
                                </p>
                            </div>
                            <button
                                onClick={() => handleViewABTests(campaigns[0])}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Open A/B Test Modal
                            </button>
                        </div>
                    </div>
                )}

                {/* Header */}
                <CampaignsHeader
                    campaignData={campaignData}
                    campaigns={campaigns}
                    filteredCampaignsCount={campaignData?.items?.length || 0}
                    selectedCampaignsCount={selectedCampaigns.size}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    typeFilter={typeFilter}
                    onTypeFilterChange={setTypeFilter}
                    aiGeneratedFilter={aiGeneratedFilter}
                    onAiGeneratedFilterChange={setAiGeneratedFilter}
                    performanceFilter={performanceFilter}
                    onPerformanceFilterChange={setPerformanceFilter}
                    dateRangeFilter={dateRangeFilter}
                    onDateRangeFilterChange={setDateRangeFilter}
                    sortBy={sortBy}
                    onSortByChange={setSortBy}
                    sortOrder={sortOrder}
                    onSortOrderChange={setSortOrder}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                    onClearFilters={clearFilters}
                    onCreateCampaign={handleCreateCampaign}
                    // Real Value Requirement
                    campaignFilter={campaignFilter}
                    toggleCampaignFilter={toggleCampaignFilter}
                    isAllStatusSelected={isAllStatusSelected}
                    onAllStatusToggle={handleAllStatusToggle}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    isLoading={campaignLoading}
                />

                {/* Bulk Operations */}
                {selectedCampaigns.size > 0 && (
                    <CampaignBulkOperations
                        selectedCount={selectedCampaigns.size}
                        selectedCampaigns={selectedCampaignObjects}
                        onBulkAction={handleBulkAction}
                        onClearSelection={handleClearSelection}
                    />
                )}

                {/* Selection row. The view toggle moved into the toolbar above. */}
                {campaignData?.items && campaignData.items.length > 0 && (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-3 border-b border-line">
                        <div className="flex items-center gap-3 min-w-0">
                            <input
                                type="checkbox"
                                checked={
                                    selectedCampaigns.size === campaignData.items.length &&
                                    campaignData.items.length > 0
                                }
                                onChange={handleSelectAll}
                                className="flex-shrink-0 rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                            />
                            <span className="min-w-0 text-sm text-content-secondary">
                                {/* "on this page": the header KPI says 174
                                    campaigns while this list shows one page of
                                    them, so an unqualified "Select all 10
                                    campaigns" read as a contradiction. */}
                                Select all{" "}
                                {campaignData.items.length}{" "}
                                on this page
                                {selectedCampaigns.size > 0 && (
                                    <span className="ml-1 text-blue-600 dark:text-blue-300 font-medium">
                                        ({selectedCampaigns.size} selected)
                                    </span>
                                )}
                            </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                            {/* Performance indicator for large datasets */}
                            {campaignData.items.length >= 100 && (
                                <div className="text-sm text-content-muted flex items-center gap-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    Virtual scrolling enabled
                                </div>
                            )}

                        </div>
                    </div>
                )}

                <CampaignView
                    searchTerm={searchTerm}
                    campaignData={campaignData}
                    campaignLoading={campaignLoading}
                    selectedCampaigns={selectedCampaigns}
                    onCampaignSelect={handleCampaignSelect}
                    onEdit={memoizedCampaignHandlers.onEdit}
                    onDelete={memoizedCampaignHandlers.onDelete}
                    onDuplicate={memoizedCampaignHandlers.onDuplicate}
                    onPause={memoizedCampaignHandlers.onPause}
                    onResume={memoizedCampaignHandlers.onResume}
                    onViewDetails={memoizedCampaignHandlers.onViewDetails}
                    onConfigureAI={memoizedCampaignHandlers.onConfigureAI}
                    onViewABTests={memoizedCampaignHandlers.onViewABTests}
                    onCreateABTest={memoizedCampaignHandlers.onCreateABTest}
                    aiAgents={aiAgents}
                    abTests={abTests}
                    viewMode={viewMode}
                    onClearSearch={() => setSearchTerm("")}
                    isError={campaignError}
                    error={campaignErrorObj}
                    onRetry={() => refetchCampaigns()}
                />

                {/* Create Campaign Modal */}
                {/* `segments` here is the hardcoded demo list this view still
                    draws its own cards from. Handing it to the modal replaced
                    the user's real audiences with "High-Value Customers" and
                    friends, and — worse — suppressed the modal's own loader,
                    which fetches real customer groups whenever the caller
                    passes none. A campaign cannot be sent to a demo segment,
                    so the modal loads its own. */}
                <CreateCampaignModal
                    isOpen={showCreateModal}
                    onClose={handleCloseCreateModal}
                    onSave={handleSaveCampaign}
                    segments={[]}
                    templates={templates}
                    aiAgents={aiAgents}
                    existingCampaigns={campaigns}
                />

                {/* Confirmation Dialogs */}
                <BulkDeleteCampaignDialog
                    isOpen={showBulkDeleteDialog}
                    onClose={() => setShowBulkDeleteDialog(false)}
                    onConfirm={handleBulkDelete}
                    selectedCampaigns={selectedCampaignObjects}
                    isLoading={bulkOperationLoading}
                />

                <DeleteCampaignDialog
                    isOpen={showDeleteDialog}
                    onClose={() => {
                        setShowDeleteDialog(false);
                        setCampaignToDelete(null);
                    }}
                    onConfirm={handleConfirmDeleteCampaign}
                    campaign={campaignToDelete}
                    isLoading={bulkOperationLoading}
                />

                <BulkStatusChangeDialog
                    isOpen={showBulkStatusDialog}
                    onClose={() => {
                        setShowBulkStatusDialog(false);
                        setBulkStatusChange(null);
                    }}
                    onConfirm={handleBulkStatusChange}
                    selectedCampaigns={selectedCampaignObjects}
                    newStatus={bulkStatusChange}
                    isLoading={bulkOperationLoading}
                />

                <BulkDuplicateDialog
                    isOpen={showBulkDuplicateDialog}
                    onClose={() => setShowBulkDuplicateDialog(false)}
                    onConfirm={handleBulkDuplicate}
                    selectedCampaigns={selectedCampaignObjects}
                    isLoading={bulkOperationLoading}
                />

                {/* A/B Testing Modal */}
                {selectedCampaignForABTest && (
                    <CampaignABTestModal
                        isOpen={showABTestModal}
                        onClose={handleCloseABTestModal}
                        campaign={selectedCampaignForABTest}
                        abTests={abTests}
                        aiAgents={aiAgents}
                        decisions={[]} // This would come from props in a real implementation
                        collaborations={[]} // This would come from props in a real implementation
                        onCreateTest={handleCreateABTest}
                        onViewTestDetails={onViewABTestDetails}
                        onApproveDecision={handleApproveDecision}
                        onRejectDecision={handleRejectDecision}
                        onImplementWinner={handleImplementWinner}
                    />
                )}

                {/* AI Configuration Modal */}
                {selectedCampaignForAIConfig && (
                    <AIConfigurationModal
                        isOpen={showAIConfigModal}
                        onClose={handleCloseAIConfigModal}
                        campaign={selectedCampaignForAIConfig}
                        aiAgent={aiAgents.find(
                            (agent) =>
                                agent.id ===
                                selectedCampaignForAIConfig.aiAgentId,
                        )}
                        onSaveConfiguration={handleSaveAIConfiguration}
                    />
                )}

                {/* Campaign Details Modal */}
                {selectedCampaignForDetails && (
                    <CampaignDetailsModal
                        isOpen={showDetailsModal}
                        onClose={handleCloseDetailsModal}
                        campaign={selectedCampaignForDetails}
                        aiAgents={aiAgents}
                        abTests={abTests}
                        segments={segments}
                        templates={templates}
                        onEdit={handleEditCampaign}
                        onDuplicate={onDuplicateCampaign}
                        onPause={onPauseCampaign}
                        onResume={onResumeCampaign}
                        onDelete={onDeleteCampaign}
                        onViewABTests={handleViewABTests}
                        onConfigureAI={handleConfigureAI}
                    />
                )}

                {/* Campaign Edit Modal */}
                {selectedCampaignForEdit && (
                    <CampaignEditModal
                        isOpen={showEditModal}
                        onClose={handleCloseEditModal}
                        onSave={handleSaveEditedCampaign}
                        campaign={selectedCampaignForEdit}
                        segments={segments}
                        templates={templates}
                        aiAgents={aiAgents}
                        existingCampaigns={campaigns}
                    />
                )}
            </Page>
        </CampaignErrorBoundary>
    );
};

export default EnhancedCampaignsView;
